import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pill, FlaskConical, ScanLine, HeartHandshake, Truck, Siren, ShieldCheck, MapPin, ArrowRight, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Extras";
import { useVisitorLoc } from "@/components/LabBooking";
import { api } from "@/lib/api";

function LiveCount() {
  const loc = useVisitorLoc();
  const [s, setS] = useState(null);
  useEffect(() => {
    let live = true; // ignore stale (no-location) responses that resolve after the located one
    api.get("/public/suvidha-stats", { params: loc || {} }).then((r) => live && setS(r.data)).catch(() => {});
    return () => { live = false; };
  }, [loc]);
  if (!s) return null;
  const n = s.pharmacies + s.labs;
  return (
    <p className="mt-5 inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-2xl sm:rounded-full bg-[#086B2D]/10 text-[#086B2D] px-4 py-2 text-sm font-semibold" data-testid="suvidha-live-count">
      <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-[#086B2D] opacity-60 animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[#086B2D]" /></span>
      {n} verified {n === 1 ? "pharmacy or lab" : "pharmacies & labs"} {s.scope === "near" ? `within ${s.radius_km} km of you` : "across Odisha"}
      <span className="basis-full sm:basis-auto pl-4 sm:pl-0 font-normal opacity-80">({s.pharmacies} {s.pharmacies === 1 ? "pharmacy" : "pharmacies"} · {s.labs} {s.labs === 1 ? "lab" : "labs"})</span>
    </p>
  );
}

const PHRASES = [
  ["Nirogyam Digital Health Network", "en"], ["ନିରୋଗ୍ୟମ ଡିଜିଟାଲ ସ୍ୱାସ୍ଥ୍ୟ ନେଟୱାର୍କ", "or"], ["निरोग्यम डिजिटल हेल्थ नेटवर्क", "hi"],
  ["Explore Nirogyam AI", "en"], ["ନିରୋଗ୍ୟମ AI ଅନୁଭବ କରନ୍ତୁ", "or"], ["निरोग्यम AI को आज़माएँ", "hi"],
  ["Say Hi to Niro – Nirogyam Network's own agentic AI at your service", "en"], ["ନିରୋଙ୍କୁ ନମସ୍କାର କୁହନ୍ତୁ – ଆପଣଙ୍କ ସେବାରେ ନିରୋଗ୍ୟମର ନିଜସ୍ୱ AI ଏଜେଣ୍ଟ", "or"],
  ["नीरो को नमस्ते कहें – आपकी सेवा में निरोग्यम का अपना AI एजेंट", "hi"],
];
const seg = typeof Intl !== "undefined" && Intl.Segmenter ? new Intl.Segmenter(undefined, { granularity: "grapheme" }) : null;
const GRAPHEMES = PHRASES.map(([t]) => (seg ? [...seg.segment(t)].map((x) => x.segment) : Array.from(t)));

export function TypeHeadline() {
  const [i, setI] = useState(0);
  const [n, setN] = useState(0);
  const [del, setDel] = useState(false);
  useEffect(() => {
    const full = GRAPHEMES[i];
    const wait = !del && n === full.length ? 2200 : del && n === 0 ? 350 : del ? 18 : 55;
    const t = setTimeout(() => {
      if (!del && n === full.length) setDel(true);
      else if (del && n === 0) { setDel(false); setI((i + 1) % PHRASES.length); }
      else setN(n + (del ? -1 : 1));
    }, wait);
    return () => clearTimeout(t);
  }, [n, del, i]);
  return (
    <p lang={PHRASES[i][1]} className="type-head font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-snug min-h-[2.8em] lg:min-h-[1.5em]" aria-label={PHRASES[i][0]} data-testid="hero-typewriter">
      <span className="type-grad">{GRAPHEMES[i].slice(0, n).join("")}</span><span className="type-caret" aria-hidden>▍</span>
    </p>
  );
}

const SERVICES = [
  [Pill, "Medicines at home", "Upload a prescription to a verified pharmacy near you – delivered within 24 hours across Bhubaneswar.", "pharmacy", "#086B2D"],
  [FlaskConical, "Free home sample collection", "Pick the lab of your choice and book tests – collection at home is free.", "lab", "#086477"],
  [ScanLine, "Radio-diagnostics booking", "Reserve X-ray, ultrasound, CT and MRI slots in advance.", "radiology", "#2412B5"],
  [HeartHandshake, "Ambulance, home-care & NPOs", "Other verified health services, all in one place.", "other", "#FF9700"],
];
const PERKS = [[ShieldCheck, "Only verified partners"], [MapPin, "Nearest first, by GPS"], [Truck, "Live order tracking"], [Siren, "Emergency priority"]];

export function SuvidhaPromo() {
  const nav = useNavigate();
  return (
    <section id="suvidha" className="relative overflow-hidden bg-[#F7F9FA] border-y border-[#E2E8ED]" data-testid="suvidha-promo">
      <div className="pointer-events-none absolute -top-24 left-1/3 h-72 w-72 rounded-full bg-[#086B2D]/10 blur-3xl blob" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-7"><p className="eyebrow">Introducing</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">Nirogyam <span className="text-[#086B2D]">Suvidha Centre</span></h2>
            <p className="mt-4 max-w-xl muted">Your neighbourhood health counter, online. Pharmacies, labs, radio-diagnostic centres and more – verified by Nirogyam, sorted nearest-first, tracked from booking to delivery.</p><LiveCount /></Reveal>
          <Reveal className="lg:col-span-5" delay={120}><div className="flex flex-wrap gap-2">{PERKS.map(([I, t]) => <span key={t} className="inline-flex items-center gap-2 rounded-full bg-white border border-[#E2E8ED] px-3 py-1.5 text-xs font-medium"><I className="h-3.5 w-3.5 text-[#086B2D]" />{t}</span>)}</div></Reveal>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICES.map(([I, t, d, k, c], i) => (
            <Reveal key={k} delay={i * 90}>
              <button onClick={() => nav(`/patient/suvidha?category=${k}`)} className="group h-full w-full text-left rounded-2xl bg-white border border-[#E2E8ED] p-6 lift relative overflow-hidden" data-testid={`suvidha-promo-${k}`}>
                <span className="absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-10 transition-transform duration-500 group-hover:scale-150" style={{ background: c }} />
                <I className="h-7 w-7" style={{ color: c }} /><p className="mt-4 font-semibold">{t}</p><p className="mt-2 text-sm muted">{d}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium" style={{ color: c }}>Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </button>
            </Reveal>))}
        </div>
        <Reveal delay={200}><div className="mt-10 flex flex-wrap items-center gap-4">
          <Button size="lg" className="rounded-full" onClick={() => nav("/patient/suvidha")} data-testid="suvidha-promo-cta">Visit Suvidha Centre <ArrowRight className="h-4 w-4 ml-2" /></Button>
          <Button size="lg" variant="outline" className="rounded-full" onClick={() => nav("/patient/suvidha?category=pharmacy")} data-testid="suvidha-promo-rx"><Upload className="h-4 w-4 mr-2" />Upload a prescription</Button>
          <a href="/partners" className="text-sm text-[#2412B5]" data-testid="suvidha-promo-partner">Own a pharmacy or lab? List it free →</a>
        </div></Reveal>
      </div>
    </section>
  );
}
